import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PrintIcon from '@mui/icons-material/Print';
import { QRCodeSVG } from 'qrcode.react';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { WarehouseService } from '../../api/services/warehouseService';
import { CollectionPointService } from '../../api/services/collectionPointService';
import { BayService } from '../../api/services/bayService';
import { WarehouseResponse } from '../../types/api/response/warehouseresponse';
import { CollectionPointResponse } from '../../types/api/response/collectionpointresponse';
import { BayResponse } from '../../types/api/response/bayresponse';
import { CreateBayRequest, UpdateBayRequest } from '../../types/api/request/bayrequest';

type SlideOverMode = 'add' | 'edit';

const emptyBayForm = {
  code: '',
  display_name: '',
  address: '',
  latitude: '',
  longitude: '',
  sort_order: '',
  qr_image_url: '',
};

export const BayManagementPage = () => {
  const dispatch = useDispatch();
  const printRef = useRef<HTMLDivElement>(null);

  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [collectionPoints, setCollectionPoints] = useState<CollectionPointResponse[]>([]);
  const [selectedCollectionPointId, setSelectedCollectionPointId] = useState<number | null>(null);
  const [bays, setBays] = useState<BayResponse[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingCollectionPoints, setLoadingCollectionPoints] = useState(false);
  const [loadingBays, setLoadingBays] = useState(false);

  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [slideOverMode, setSlideOverMode] = useState<SlideOverMode>('add');
  const [editingBay, setEditingBay] = useState<BayResponse | null>(null);
  const [form, setForm] = useState(emptyBayForm);
  const [saving, setSaving] = useState(false);

  const [confirmDeactivate, setConfirmDeactivate] = useState<BayResponse | null>(null);
  const [qrBay, setQrBay] = useState<BayResponse | null>(null);

  const fetchWarehouses = useCallback(async () => {
    setLoadingWarehouses(true);
    try {
      const data = await WarehouseService.listWarehouses(false);
      setWarehouses(data);
      if (data.length > 0 && selectedWarehouseId === null) {
        setSelectedWarehouseId(data[0].id);
      }
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load warehouses.',
          severity: 'error',
        }),
      );
    } finally {
      setLoadingWarehouses(false);
    }
  }, [dispatch, selectedWarehouseId]);

  const fetchCollectionPoints = useCallback(
    async (warehouseId: number) => {
      setLoadingCollectionPoints(true);
      setCollectionPoints([]);
      setSelectedCollectionPointId(null);
      setBays([]);
      try {
        const data = await CollectionPointService.listCollectionPoints(warehouseId, false);
        setCollectionPoints(data);
        if (data.length > 0) {
          setSelectedCollectionPointId(data[0].id);
        }
      } catch (e: any) {
        dispatch(
          showNotification({
            message: e?.response?.data?.message ?? 'Failed to load collection points.',
            severity: 'error',
          }),
        );
      } finally {
        setLoadingCollectionPoints(false);
      }
    },
    [dispatch],
  );

  const fetchBays = useCallback(
    async (collectionPointId: number) => {
      setLoadingBays(true);
      try {
        const data = await BayService.listBays(collectionPointId, false);
        setBays(data);
      } catch (e: any) {
        dispatch(
          showNotification({
            message: e?.response?.data?.message ?? 'Failed to load bays.',
            severity: 'error',
          }),
        );
      } finally {
        setLoadingBays(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    if (selectedWarehouseId !== null) {
      fetchCollectionPoints(selectedWarehouseId);
    }
  }, [selectedWarehouseId, fetchCollectionPoints]);

  useEffect(() => {
    if (selectedCollectionPointId !== null) {
      fetchBays(selectedCollectionPointId);
    }
  }, [selectedCollectionPointId, fetchBays]);

  const openAdd = () => {
    setSlideOverMode('add');
    setEditingBay(null);
    const nextSortOrder = bays.length > 0 ? Math.max(...bays.map((b) => b.sort_order)) + 1 : 1;
    setForm({ ...emptyBayForm, sort_order: String(nextSortOrder) });
    setSlideOverOpen(true);
  };

  const openEdit = (b: BayResponse) => {
    setSlideOverMode('edit');
    setEditingBay(b);
    setForm({
      code: b.code,
      display_name: b.display_name,
      address: b.address ?? '',
      latitude: String(b.latitude),
      longitude: String(b.longitude),
      sort_order: String(b.sort_order),
      qr_image_url: b.qr_image_url ?? '',
    });
    setSlideOverOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedCollectionPointId) return;

    const missingFields: string[] = [];
    if (slideOverMode === 'add' && !form.code.trim()) missingFields.push('Bay Code');
    if (!form.display_name.trim()) missingFields.push('Display Name');
    if (!form.address.trim()) missingFields.push('Address / Location');
    if (!form.latitude.trim()) missingFields.push('Latitude');
    if (!form.longitude.trim()) missingFields.push('Longitude');
    if (!form.sort_order.trim()) missingFields.push('Sort Order');
    if (missingFields.length > 0) {
      dispatch(
        showNotification({
          message: `Required fields are missing: ${missingFields.join(', ')}.`,
          severity: 'error',
        }),
      );
      return;
    }

    setSaving(true);
    try {
      if (slideOverMode === 'add') {
        const body: CreateBayRequest = {
          collection_point_id: selectedCollectionPointId,
          code: form.code,
          display_name: form.display_name,
          address: form.address,
          latitude: parseFloat(form.latitude),
          longitude: parseFloat(form.longitude),
          sort_order: parseInt(form.sort_order),
          qr_image_url: form.qr_image_url || undefined,
        };
        await BayService.createBay(body);
        dispatch(showNotification({ message: 'Bay created successfully.', severity: 'success' }));
      } else {
        const body: UpdateBayRequest = {
          display_name: form.display_name,
          address: form.address,
          latitude: parseFloat(form.latitude),
          longitude: parseFloat(form.longitude),
          sort_order: parseInt(form.sort_order),
          qr_image_url: form.qr_image_url || undefined,
        };
        await BayService.updateBay(editingBay!.id, body);
        dispatch(showNotification({ message: 'Bay updated.', severity: 'success' }));
      }
      setSlideOverOpen(false);
      fetchBays(selectedCollectionPointId);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to save bay.',
          severity: 'error',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusClick = (b: BayResponse) => {
    if (b.is_active) {
      setConfirmDeactivate(b);
    } else {
      toggleBayStatus(b, true);
    }
  };

  const toggleBayStatus = async (b: BayResponse, newStatus: boolean) => {
    try {
      await BayService.updateBay(b.id, { is_active: newStatus });
      if (selectedCollectionPointId) fetchBays(selectedCollectionPointId);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to update bay status.',
          severity: 'error',
        }),
      );
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(
      `<html><head><title>Bay QR Code</title><style>body{display:flex;justify-content:center;align-items:center;height:100vh;margin:0;}</style></head><body>${printContent.innerHTML}</body></html>`,
    );
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  return (
    <Box>
      {/* Two-level selector: Warehouse → Collection Point */}
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        {loadingWarehouses ? (
          <CircularProgress size={24} />
        ) : warehouses.length === 0 ? (
          <Typography color="text.secondary">
            No warehouses found. Create a warehouse to get started.
          </Typography>
        ) : (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Warehouse</InputLabel>
            <Select
              label="Warehouse"
              value={selectedWarehouseId ?? ''}
              onChange={(e) => setSelectedWarehouseId(Number(e.target.value))}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedWarehouseId !== null &&
          (loadingCollectionPoints ? (
            <CircularProgress size={24} />
          ) : collectionPoints.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No collection points for this warehouse. Create a collection point first.
            </Typography>
          ) : (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Collection Point</InputLabel>
              <Select
                label="Collection Point"
                value={selectedCollectionPointId ?? ''}
                onChange={(e) => setSelectedCollectionPointId(Number(e.target.value))}
              >
                {collectionPoints.map((cp) => (
                  <MenuItem key={cp.id} value={cp.id}>
                    {cp.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

        {selectedCollectionPointId !== null && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Bay
          </Button>
        )}
      </Box>

      {/* Bays table */}
      {loadingBays && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loadingBays && selectedCollectionPointId !== null && bays.length === 0 && (
        <Box py={8} textAlign="center" color="text.secondary">
          <Typography variant="h6">No bays found</Typography>
          <Typography variant="body2">
            Add a bay to this collection point to get started.
          </Typography>
        </Box>
      )}

      {!loadingBays && bays.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sort</TableCell>
                <TableCell>Bay</TableCell>
                <TableCell>Code / QR</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Coordinates</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bays.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>{b.sort_order}</TableCell>
                  <TableCell>{b.display_name}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <code style={{ fontSize: 12 }}>{b.code}</code>
                      <Tooltip title="View QR Code">
                        <IconButton size="small" onClick={() => setQrBay(b)}>
                          <QrCodeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{b.address ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {parseFloat(String(b.latitude)).toFixed(3)},{' '}
                    {parseFloat(String(b.longitude)).toFixed(3)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={b.is_active ? 'Active' : 'Inactive'}
                      color={b.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleStatusClick(b)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openEdit(b)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit modal */}
      <Dialog open={slideOverOpen} onClose={() => setSlideOverOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{slideOverMode === 'add' ? 'Add Bay' : 'Edit Bay'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Bay Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={slideOverMode === 'edit'}
              required
              helperText={
                slideOverMode === 'add'
                  ? 'This is the QR code value. It cannot be changed after creation. Recommended format: BAY-{number}-CP-{collection-point-code}'
                  : undefined
              }
              fullWidth
            />
            <TextField
              label="Display Name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Address / Location"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Latitude"
              type="number"
              value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Longitude"
              type="number"
              value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Sort Order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="QR Image URL (optional)"
              value={form.qr_image_url}
              onChange={(e) => setForm({ ...form, qr_image_url: e.target.value })}
              helperText="Leave blank to render QR code in-browser from the code value."
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSlideOverOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={!!confirmDeactivate} onClose={() => setConfirmDeactivate(null)}>
        <DialogTitle>Deactivate Bay</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivating {confirmDeactivate?.display_name} will prevent it from being assigned to
            new customer arrivals. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivate(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              if (confirmDeactivate) toggleBayStatus(confirmDeactivate, false);
              setConfirmDeactivate(null);
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code modal */}
      <Dialog open={!!qrBay} onClose={() => setQrBay(null)} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code — {qrBay?.display_name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={1}>
            <div ref={printRef}>
              {qrBay?.qr_image_url ? (
                <img
                  src={qrBay.qr_image_url}
                  alt={`QR for ${qrBay.code}`}
                  style={{ maxWidth: 220, maxHeight: 220 }}
                />
              ) : (
                qrBay && <QRCodeSVG value={qrBay.code} size={220} />
              )}
            </div>
            <Typography variant="caption" color="text.secondary">
              {qrBay?.code}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrBay(null)}>Close</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
